import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
} from "@mui/material";
import axios from "axios";
import API_BASE_URL from "../../utils/api";
import QuillEditor from "../../components/QuillEditor/QuillEditor";
import { useAuth } from "../Context/AuthContext";
const formats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "align",
  "link",

  "code-block",
];

const Threads = ({ userId, onThreadPosted, draftData }) => {
  const [title, setTitle] = useState(draftData?.title || "");
  const [content, setContent] = useState(draftData?.content || "");
  const [attachments, setAttachments] = useState([]);
  const [isDraft, setIsDraft] = useState(false);
  const quillRef = useRef(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [visibility, setVisibility] = useState(draftData?.visibility || "all");
  const [staff, setStaff] = useState([]);
  const [excludedStaff, setExcludedStaff] = useState([]);
  const handleFileChange = (e) => {
    setAttachments([...e.target.files]);
  };
  const { user } = useAuth();
  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/threads/staff`, {
        withCredentials: true,
      });
      setStaff(res.data || []);
      setExcludedStaff([]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("userId", userId);
    formData.append("isDraft", isDraft);
    formData.append("visibility", visibility);

    if (visibility === "staff") {
      formData.append("excludedStaff", JSON.stringify(excludedStaff));
    }

    attachments.forEach((file) => formData.append("attachments", file));

    const res = await axios.post(`${API_BASE_URL}/threads/create`, formData);
    onThreadPosted(res.data);
    setSnackbarMessage("Thread posted successfully");
    setSnackbarOpen(true);
    setTitle("");
    setContent("");
    setAttachments([]);
    setIsDraft(false);
    setExcludedStaff([]);
  };

  useEffect(() => {
    if (visibility === "staff") {
      fetchStaff();
    }
  }, [visibility]);

  // If draftData is loaded, re-hydrate
  useEffect(() => {
    if (draftData) {
      setTitle(draftData.title || "");
      setContent(draftData.content || "");
      setVisibility(
        draftData.visibility === "workshop" && !user?.workshopAccess
          ? "all"
          : draftData.visibility || "all"
      );
      // If you stored notifyExclude on thread, you could pre-fill excludedStaff here too
      if (Array.isArray(draftData.notifyExclude)) {
        setExcludedStaff(draftData.notifyExclude.map(String));
      }
    }
  }, [draftData, user]);

  // Quill modules
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ["16px"] }],

        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  const includedStaff = staff.filter(
    (s) => !excludedStaff.includes(String(s._id))
  );
  const removedStaff = staff.filter((s) =>
    excludedStaff.includes(String(s._id))
  );

  return (
    <Box>
      <Typography variant="h6">Start a Thread</Typography>
      <TextField
        fullWidth
        label="What can we do to help you?"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* Replace TextArea with QuillEditor */}
      <Box
        sx={{
          mb: 3,
          "& .ql-editor": {
            minHeight: 80,
            cursor: "text",
            lineHeight: 1.8,
            fontSize: "16px",
          },
          "& .ql-editor.ql-blank::before": {
            fontStyle: "normal !important",
            fontSize: "16px !important",
            color: "#999",
          },
        }}
      >
        <QuillEditor
          ref={quillRef}
          value={content}
          onChange={setContent}
          modules={modules}
          formats={formats}
          placeholder="Provide details so members of the Hub or users of the website can provide answers
attachments upload materials or examples"
        />
      </Box>
      <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
        <InputLabel id="visibility-label">Who can see this thread?</InputLabel>
        <Select
          labelId="visibility-label"
          id="visibility"
          label="Who can see this thread?"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
        >
          <MenuItem value="all">All Registered Users</MenuItem>

          {user?.workshopAccess && (
            <MenuItem value="workshop"> Only Workshop Members</MenuItem>
          )}
          <MenuItem value="staff">Only Hub Staff (Admins)</MenuItem>
        </Select>
      </FormControl>
      {visibility === "staff" && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Staff who will be notified (click × to exclude):
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {includedStaff.map((s) => (
              <Chip
                key={s._id}
                avatar={
                  <Avatar src={s.profileImage || undefined} alt={s.name} />
                }
                label={s.name}
                onDelete={() =>
                  setExcludedStaff((prev) => [...prev, String(s._id)])
                }
              />
            ))}

            {!includedStaff.length && (
              <Typography variant="body2" color="text.secondary">
                (No staff selected — this post will still be visible to admins,
                but no email will be sent.)
              </Typography>
            )}
          </Box>

          {removedStaff.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Excluded:
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                {removedStaff.map((s) => (
                  <Chip
                    key={s._id}
                    variant="outlined"
                    avatar={
                      <Avatar src={s.profileImage || undefined} alt={s.name} />
                    }
                    label={s.name} // Clicking adds back in
                    onClick={() =>
                      setExcludedStaff((prev) =>
                        prev.filter((id) => id !== String(s._id))
                      )
                    }
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
      <input
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ marginTop: 10 }}
      />

      <FormControlLabel
        control={
          <Switch checked={isDraft} onChange={() => setIsDraft(!isDraft)} />
        }
        label="Save as Draft"
      />
      <Button onClick={handleSubmit} variant="contained" sx={{ mt: 2 }}>
        {isDraft ? "Save Draft" : "Post Thread"}
      </Button>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%", backgroundColor: "green", color: "#fff" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Threads;
